import { groupEvidenceByDetail, safeStatus, STATUS_ICON, STATUS_LABEL, STATUS_TONE } from './evidence';

import { cn } from '@/lib/cn';
import type { ReadinessEvidenceItem } from '@/lib/api/readiness';

export interface EvidenceItemListProps {
  items: ReadinessEvidenceItem[];
  /**
   * Builds the href for an item. Returns undefined for anything this surface
   * has no route for — a broken link is worse than plain text.
   */
  linkFor?: (item: ReadinessEvidenceItem) => string | undefined;
  onNavigate?: (href: string) => void;
  /**
   * Promote `detail` to a group heading past the threshold. Off for the card's
   * five-row preview, where there is nothing to de-duplicate.
   */
  groupByDetail?: boolean;
  className?: string;
}

/**
 * The records themselves, in the API's worst-first order.
 *
 * Shared by the card's preview and the drill-down drawer so a row cannot look
 * like one thing in one place and another in the other. Past a threshold the
 * repeated module name is lifted into a heading — on a 327-topic list, the
 * module was previously written out 327 times and was the loudest text on the
 * page.
 */
export function EvidenceItemList({
  items,
  linkFor,
  onNavigate,
  groupByDetail = false,
  className,
}: EvidenceItemListProps) {
  const groups = groupByDetail ? groupEvidenceByDetail(items) : null;

  if (!groups) {
    return (
      <ul className={cn('space-y-1.5', className)}>
        {items.map((item, i) => (
          <EvidenceRow
            key={`${item.label}-${item.link_id ?? i}`}
            item={item}
            href={linkFor?.(item)}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {groups.map((group) => (
        <section key={group.key} aria-label={group.label}>
          {/*
            Fully opaque, and spanning the scroll container's own width.

            This was `bg-background/95` plus `backdrop-blur`: five percent of
            every row scrolling underneath came through the heading, and the
            blur smeared it, so a module title sat on top of a ghost of the row
            behind it. A sticky header has one job — to hide what passes under
            it — and a translucent one cannot do it. The negative inset cancels
            the scroll container's padding so the band reaches both edges;
            without it the row's own hover padding poked out either side.

            `-top-3` rather than `top-0` for the same reason vertically: a sticky
            offset resolves against the scroll container's CONTENT box, and that
            container has 12px of top padding, so `top-0` pinned the heading 12px
            below the visible edge and left a band where rows slid past in full
            view. The negative offset pins it flush to the scrollport.
          */}
          <h4 className="sticky -top-3 z-20 -mx-4 bg-background px-4 py-1.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground sm:-mx-5 sm:px-5">
            {group.label}
            <span className="ml-1.5 font-semibold normal-case tracking-normal text-muted-foreground/70">
              {group.items.length}
            </span>
          </h4>
          <ul className="space-y-1.5">
            {group.items.map((item, i) => (
              <EvidenceRow
                key={`${item.label}-${item.link_id ?? i}`}
                item={item}
                href={linkFor?.(item)}
                onNavigate={onNavigate}
                // The module is the heading above; repeating it per row is the
                // noise this grouping exists to remove.
                hideDetail
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function EvidenceRow({
  item,
  href,
  onNavigate,
  hideDetail = false,
}: {
  item: ReadinessEvidenceItem;
  href?: string;
  onNavigate?: (href: string) => void;
  hideDetail?: boolean;
}) {
  const status = safeStatus(item.status);
  const Icon = STATUS_ICON[status];

  // All three cells align on the label's FIRST line, not on the row's centre.
  //
  // Centring looked right on the drawer's single-line rows and wrong in the
  // card preview, where a row carries a detail line underneath: the status
  // drifted to sit between the title and its detail, belonging to neither.
  //
  // So: `items-start`, and every cell gets the same 20px line box — the icon
  // centred inside a box of that height, the label and value sharing the same
  // font-size and `leading-5`. Matching the boxes is what the old `mt-[1px]`
  // nudge was trying and failing to fake.
  return (
    <li className="flex items-start gap-2 rounded px-1 py-[3px] text-[11.5px] leading-5 hover:bg-muted/50">
      <span className="flex h-5 flex-none items-center" aria-hidden="true">
        <Icon className={cn('h-3.5 w-3.5', STATUS_TONE[status])} />
      </span>
      <div className="min-w-0 flex-1 leading-5">
        {href ? (
          <a
            href={href}
            onClick={(e) => {
              // Leave modified clicks to the browser (new tab, etc.).
              if (!onNavigate || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
              e.preventDefault();
              onNavigate(href);
            }}
            // Underline on hover only. Ninety linked rows all underlined at
            // rest turned the drill-down into a wall of rules with no way to
            // pick out the one being read; the affordance is not lost, it just
            // stops shouting.
            className="break-words text-[11.5px] font-medium leading-5 text-foreground hover:text-primary hover:underline hover:underline-offset-2"
          >
            {item.label}
          </a>
        ) : (
          <span className="break-words text-[11.5px] font-medium leading-5 text-foreground">{item.label}</span>
        )}
        {item.detail && !hideDetail && (
          <p className="break-words text-[10.5px] leading-snug text-muted-foreground">{item.detail}</p>
        )}
      </div>
      {/*
        A fixed, narrow value column: on a wide screen the value used to be
        flushed to the viewport edge, which put it a screen's width from the
        label it belonged to and made the pair unreadable.
      */}
      <span
        className={cn(
          'w-[5.5rem] flex-none break-words text-right font-semibold sm:w-28',
          STATUS_TONE[status],
        )}
      >
        {item.value}
        <span className="sr-only"> — {STATUS_LABEL[status]}</span>
      </span>
    </li>
  );
}
